import get from 'lodash/get'
import { debug } from '@bt-react/libs/Logger'
import usernameLib from '../username'
import apiGateway from '../../services/apiGateway'
import splitParams from '../splitParams'


type LoggingService = 'PresenterService' | 'WatchService'
type LoggingEvent = 'GigStart' |
  'GigStartSuccess' |
  'GigStartError' |
  'GigEnd' |
  'GigEndSuccess' |
  'GigEndError' |
  'GigExtend' |
  'GigExtendSuccess' |
  'GigExtendError' |
  'GetAvailableFeatures' |
  'PlayInAdded' |
  'PlayInRemoved' |
  'PlayInStarted' |
  'PlayInStartRequested' |
  'PlayInPaused' |
  'PlayInPauseRequested' |
  'PlayInSeeked' |
  'PlayInSeekRequested' |
  'PlayInEnd' |
  'PlayInEndRequested' |
  'PlayInListFetching' |
  'PlayInGettingUrlFromSources' |
  'PlayInRemoteStatus' |
  'PlayInPlayerError' |
  'PlayInScreenSources' |
  'PlayInScreenSourcesChanged' |
  'PlayInSetSources' |
  'MediaTypeSelected' |
  'PresenterMuted' |
  'PresenterUnMuted' |
  'ParticipantDisconnected' |
  'ParticipantAudioTrackSwitchedOff' |
  'ParticipantAudioTrackDisabled' | 
  'ParticipantAudioTrackUnsubscribed' |
  'ParticipantAudioTrackSubscriptionFailed' |
  'BridgeStatus' |
  'SelectEnabledVideoTracksForPresenter' | 
  'SelectEnabledVideoTracks' |
  'GetTracksByPresenter' | 
  'SelectEnabledAudioTracks' | 
  'ParticipantConnected' | 
  'ParticipantInformationUpdated' |
  'AppLoad' | 
  'AppUnload' | 
  'EnterAsPresenterClicked' | 
  'EnterAsObserverClicked' |
  'EnterPresenterScreenClicked' |
  'AccessModalDisplayed' |
  'PreviewScreenEnterClick' |
  'ShowDeadMansHandle' |
  'SlidesScreen' |
  'SystemPreview' |
  'SystemPreviewVideoClick' |
  'SystemPreviewAudioClick' |
  'SystemPreviewSettingsClick' |
  'SystemPreviewMicAndCamSettings' |
  'SystemSettingsDisplayed' | 
  'SystemSettingsBackgroundChange' |
  'SystemSettingsTabChange' |
  'SystemSettingsMicrophoneChange' |
  'SystemSettingsWebcamChange' |
  'SystemSettingsCancel' |
  'SystemSettingsConfirm' |
  'SystemSettingsAudioSourceChange' |
  'PresenterReport' |
  'PresenterChat' |
  'WarningDisplay' |
  'WarningDisplayClose' |
  'TwilioParticipantReconnecting' |
  'PresenterHidden' |
  'PresenterUnHidden' |
  'PresenterFocused' |
  'PresenterUnFocused' |
  'PresenterVisibilityFocused' |
  'PresenterVisibilityUnFocused' |
  'DocumentUpdated' |
  'DocumentsFetched' |
  'StreamEndClicked' |
  'StreamStartClicked'|
  'Q&AConversationConnection' |
  'LocalStorageSetting' |
  'ParticipantDisconnectedAction' |
  'ParticipantVideoTrackUnsubscribed' |
  'ParticipantVideoTrackDisabled' |
  'ParticipantVideoTrackSwitchedOff' |
  'ParticipantVideoTrackSubscriptionFailed' |
  'ParticipantAudioTrackEnabled' |
  'ParticipantVideoTrackEnabled' |
  'ParticipantConnectedDialIn' |
  'DisconnectedLocal' |
  'ReconnectingLocal' |
  'ReconnectedLocal' |
  'ParticipantReconnected' |
  'ParticipantReconnecting' |
  'ParticipantTrackMessage' |
  'ParticipantAudioTrackPublished' |
  'ParticipantVideoTrackPublished' |
  'ParticipantTrackStarted' |
  'ParticipantAudioTrackSubscribed' |
  'ParticipantVideoTrackSubscribed' |
  'ParticipantAudioTrackSwitchedOn' |
  'ParticipantVideoTrackSwitchedOn' |
  'ParticipantAudioTrackUnpublished' |
  'ParticipantVideoTrackUnpublished' |
  'ReconnectionModalDisplayed' |
  'ReconnectionModalAutomaticReconnect' |
  'ReconnectionModalButtonClicked' |
  'RollPlayer' |
  'SyncConnectionStateChange' |
  'TwilioTokenRenewal' |
  'UpdateAudioMute' |
  'UserLeavingThePage' |
  'UserSettingsFetch' |
  'UserSettingsSettingBackgroundError' |
  'UserSettingsVirtualBackgroundUploadError' |
  'ReconnectionModalDisplayedNoTwilError' |
  'EventStreamFetching' |
  'FetchCommunicationError'

type LogLevel = 'info' | 'debug' | 'warn' | 'error' | 'report'

interface AuditInterface {
  addEntry(service: LoggingService, event: LoggingEvent, message: string, level?: LogLevel, identity?: string): void
  init(roomname: string): void
}

export interface AuditEntry {
  loggingService: LoggingService
  loggingEvent: LoggingEvent
  loggingText: string
  loggingLevel: number
}

class Audit implements AuditInterface {

  private _instance
  private _roomname

  constructor () {

    if (!this._instance) this._instance = this

    return this._instance

  }

  init = (roomname: string) => {

    this._roomname = roomname

  }

  private getLogLevel (logLevelType: LogLevel): number {

    switch (logLevelType) {

      case 'info':
        return 1
      case 'warn':
        return 2
      case 'error':
        return 3
      case 'report':
        return 9
      case 'debug': default:
        return 0

    }

  }

  addEntry = async (service: LoggingService, event: LoggingEvent, message: string, level: LogLevel = 'info', identity?: string, catchErrors = true): Promise<void> => {

    if (!this._roomname) {
      debug(`Audit Roomname is not set`)
      return

    }

    const logLevel = this.getLogLevel(level)
    const record: AuditEntry = {
      loggingService: service,
      loggingEvent: event,
      loggingText: identity ? `${usernameLib.getPureUsername(identity)} - ${message}` : message,
      loggingLevel: logLevel
    }

    const params = splitParams(document.location.search)

    if (catchErrors) {
      await apiGateway.addAuditEntry(this._roomname, record, params)
        .catch(e => {
          debug(`Failed to send audit record: ${get(e, 'message', '')}`)

        })
    } else {
      await apiGateway.addAuditEntry(this._roomname, record, params)
    }

  }

}

export default new Audit()
